import { useMemo, useState } from 'react'
import { KeyRound, Shield, UserCheck, UserCog, Users } from 'lucide-react'
import { Button, Divider, EmptyHint } from '../ui/Panels'
import { StateBadge, StateDot } from '../ui/State'
import { fmtAlt } from '../../lib/format'
import type { ConsoleUser, Role, UiState } from '../../types'

/* ============================================================================
 * УПРАВЛЕНИЕ ПОЛЬЗОВАТЕЛЯМИ: РОЛИ И РАБОЧИЙ ПРОЦЕСС
 *
 * Роли консоли:
 *   • Air Boss              — руководитель воздушного боя, полный доступ;
 *   • Airspace Coordinator  — координатор воздушного пространства;
 *   • Пилот                 — оператор БВС, подача деклараций;
 *   • Администратор        — управление учётными записями и сертификатами.
 *
 * Привязка актива — по СЕРИЙНОМУ НОМЕРУ (аналог учёта по Part 107).
 * ========================================================================= */

const ROLES: Role[] = ['Air Boss', 'Airspace Coordinator', 'Пилот', 'Администратор']

/** Цвет состояния строки по роли и наличию привязанного актива */
function userState(u: ConsoleUser): UiState {
  if (!u.online) return 'pending'
  if (u.role === 'Пилот' && !u.assetSerial) return 'warning'
  if (u.role === 'Пилот' && !u.certificateNo) return 'warning'
  return 'approved'
}

/** Начальный набор пользователей консоли (демонстрационные данные) */
export const SEED_USERS: ConsoleUser[] = [
  {
    id: 'u-001',
    name: 'Старший диспетчер ИВО',
    role: 'Air Boss',
    assetSerial: 'UTM-AB-0001',
    certificateNo: 'АБ-2024-00117',
    maxAltM: 150,
    online: true,
  },
  {
    id: 'u-002',
    name: 'Координатор пространства',
    role: 'Airspace Coordinator',
    maxAltM: 150,
    online: true,
  },
  {
    id: 'u-003',
    name: 'Пилот БВС (внешний)',
    role: 'Пилот',
    assetSerial: 'GEOS-MINI-4471',
    certificateNo: 'ПИЛ-2023-00982',
    maxAltM: 120,
    online: false,
  },
  {
    id: 'u-004',
    name: 'Администратор системы',
    role: 'Администратор',
    online: true,
  },
]

export function UsersRolesPanel({
  users,
  onChangeRole,
  onBindAsset,
}: {
  users: ConsoleUser[]
  onChangeRole: (id: string, role: Role) => void
  onBindAsset: (id: string, serial: string) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [serialDraft, setSerialDraft] = useState('')

  const counts = useMemo(() => {
    const out: Record<string, number> = {}
    for (const u of users) out[u.role] = (out[u.role] ?? 0) + 1
    return out
  }, [users])

  return (
    <div className="space-y-2 p-2.5">
      {/* Сводка по ролям */}
      <div className="ut-stated ut-state-selected rounded-md border px-2.5 py-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold">
          <Users size={12} aria-hidden />
          Роли и рабочий процесс
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink-muted">
          {ROLES.map((r) => (
            <span key={r} className="flex items-center gap-1">
              <StateDot state="approved" />
              {r}: <b className="ut-metric">{counts[r] ?? 0}</b>
            </span>
          ))}
        </div>
      </div>

      <Divider label={`Пользователи (${users.length})`} />

      {users.length === 0 ? (
        <EmptyHint>Учётные записи не загружены</EmptyHint>
      ) : (
        <div className="space-y-1">
          {users.map((u) => {
            const st = userState(u)
            const isEditing = editing === u.id
            const isPilot = u.role === 'Пилот'
            return (
              <div key={u.id} className={`rounded-md border px-2 py-1.5 ut-stated ut-state-${st}`}>
                <div className="flex items-center gap-2">
                  <StateDot state={st} pulsing={!u.online} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[11px] font-semibold">{u.name}</div>
                    <div className="ut-metric truncate text-[10px] text-ink-muted">
                      {u.online ? 'в сети' : 'не в сети'} · {u.id}
                    </div>
                  </div>
                  <StateBadge state={u.online ? 'approved' : 'pending'}>
                    {u.online ? 'ОНЛАЙН' : 'ОФЛАЙН'}
                  </StateBadge>
                </div>

                {/* Роль */}
                <div className="mt-1.5 flex items-center gap-1.5">
                  <UserCog size={11} className="shrink-0 text-ink-muted" aria-hidden />
                  <select
                    className="ut-select"
                    value={u.role}
                    onChange={(e) => onChangeRole(u.id, e.target.value as Role)}
                    aria-label={`Роль пользователя ${u.name}`}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Привязка актива по серийному номеру */}
                {isPilot && (
                  <div className="mt-1.5">
                    <div className="mb-0.5 flex items-center gap-1.5 text-[10px] text-ink-muted">
                      <KeyRound size={10} aria-hidden />
                      Актив по серийному номеру
                    </div>
                    {isEditing ? (
                      <div className="flex gap-1.5">
                        <input
                          className="ut-input ut-metric"
                          placeholder="GEOS-MINI-4471"
                          value={serialDraft}
                          autoFocus
                          onChange={(e) => setSerialDraft(e.target.value)}
                        />
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={serialDraft.trim() === ''}
                          onClick={() => {
                            onBindAsset(u.id, serialDraft.trim())
                            setEditing(null)
                            setSerialDraft('')
                          }}
                        >
                          OK
                        </Button>
                        <Button size="sm" onClick={() => setEditing(null)}>
                          Отмена
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`ut-metric flex-1 truncate rounded border px-1.5 py-1 text-[10px] ${
                            u.assetSerial
                              ? 'ut-stated ut-state-approved'
                              : 'ut-stated ut-state-warning'
                          }`}
                        >
                          {u.assetSerial ?? 'НЕ ПРИВЯЗАН'}
                        </span>
                        <Button
                          size="sm"
                          icon={KeyRound}
                          onClick={() => {
                            setEditing(u.id)
                            setSerialDraft(u.assetSerial ?? '')
                          }}
                        >
                          {u.assetSerial ? 'Заменить' : 'Привязать'}
                        </Button>
                      </div>
                    )}
                    <div className="mt-1 ut-metric flex flex-wrap gap-x-3 text-[10px] text-ink-muted">
                      <span>Сертификат: {u.certificateNo ?? '—'}</span>
                      <span>Допуск по высоте: {u.maxAltM !== undefined ? fmtAlt(u.maxAltM) : '—'}</span>
                    </div>
                  </div>
                )}

                {!isPilot && (
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-ink-muted">
                    <Shield size={10} aria-hidden />
                    Связанных активов нет
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="flex items-start gap-1.5 rounded-md border border-line px-2 py-1.5 text-[10px] leading-snug text-ink-muted">
        <UserCheck size={11} className="mt-0.5 shrink-0" aria-hidden />
        Пилот может работать только с классом БВС, указанным в его сертификате, и в
        пределах допустимой высоты. Привязка актива выполняется по серийному номеру.
      </div>
    </div>
  )
}

/** Иконка раздела управления */
export const ManageIcon = Users
